import { useNavigate } from '@tanstack/react-router';
import { httpsCallable } from 'firebase/functions';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { renderChapterSections } from '@/features/chapters/lib/chapter-sections';
import { sanitizeAssembledProse } from '@/features/chapters/lib/sanitize-assembled-prose';
import { useFirebase } from '@/firebase';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { calculateWordCount } from '@/lib/text-service';
import { ContentDocument, NarrativeSummary } from '@/lib/types';

export function usePublishChapter() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, functions } = useFirebase();
  const {
    novel,
    contentDao,
    getNewId,
    saveItem,
    updateNarrativeSummary,
    updateNarrativeItem,
  } = useNovelRepository();

  const [isAssembling, setIsAssembling] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [unreadyData, setUnreadyData] = useState<{
    chapterId: string;
    scenes: NarrativeSummary[];
  } | null>(null);

  const assembleFinalEdition = async (chapterId: string, force = false, navigateAfter = true) => {
    if (!novel.id || !contentDao || !getNewId) return null;

    // 1. Fetch and sort all active scenes for this chapter
    const scenes = (Object.values(novel.summaries?.scenes || {}) as NarrativeSummary[])
      .filter((s) => s.chapterId === chapterId && !s.isArchive)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    // Both terminal phases are ready for chapter assembly.
    const terminalPhases = new Set(['approved', 'ready']);
    const unreadyScenes = scenes.filter((s) => !terminalPhases.has((s.status?.phase || 'idle').toLowerCase()));

    if (unreadyScenes.length > 0 && !force) {
      setUnreadyData({ chapterId, scenes: unreadyScenes });
      return null;
    }

    setIsAssembling(true);
    try {
      const chapter = novel.summaries?.chapters?.[chapterId] as NarrativeSummary | undefined;
      if (!chapter) {
        throw new Error('Chapter not found');
      }

      // 2. Fetch and concatenate prose parts
      const proseParts: Record<string, string> = {};
      const compiledSceneVersions: Record<string, string> = {};
      const sceneVariantRefs: Record<string, { contentId: string; version: number }> = {};

      for (const scene of scenes) {
        if (scene.mainContentId) {
          try {
            const contentDoc = await contentDao.getContent<ContentDocument>(novel.ownerId, novel, scene.mainContentId);
            if (contentDoc) {
              proseParts[scene.id] = sanitizeAssembledProse(contentDoc.scratchpad || '');
              sceneVariantRefs[scene.id] = {
                contentId: scene.mainContentId,
                version: contentDoc.scratchpadVersion ?? 0,
              };
              await updateNarrativeItem('scenes', scene.id, {
                contentVariantVersions: {
                  ...(scene.contentVariantVersions || {}),
                  [scene.mainContentId]: contentDoc.scratchpadVersion ?? 0,
                },
              });
            }
          } catch (e) {
            console.error(`Failed to fetch scene content for ${scene.id}:`, e);
          }
        }
        // Save current version timestamp
        compiledSceneVersions[scene.id] = scene.updatedAt;
      }

      const compiledProse = renderChapterSections(scenes.map((scene) => ({
        type: 'scene',
        id: scene.id,
        sceneId: scene.id,
        html: proseParts[scene.id] || '<p></p>',
      })));
      const wordCount = calculateWordCount(compiledProse);
      let contentId = chapter.mainContentId;

      if (contentId) {
        // Update existing document
        await contentDao.updateContent(novel.ownerId, novel, contentId, {
          scratchpad: compiledProse,
          wordCount,
          sceneIds: scenes.map((s) => s.id),
        });
      } else {
        // Create new document
        contentId = getNewId();
        const newDoc: ContentDocument = {
          id: contentId,
          name: chapter.name || 'Untitled Chapter',
          scratchpad: compiledProse,
          scratchpadVersion: 0,
          currentDayKey: new Date().toISOString().split('T')[0],
          sceneIds: scenes.map((s) => s.id),
          chapterId: chapterId,
          wordCount,
          audit: {
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }
        };
        await contentDao.createContent(novel.ownerId, novel, newDoc);
      }

      // 3. Update Chapter Summary and Document
      const now = new Date().toISOString();
      const updatedChapter: NarrativeSummary = {
        ...chapter,
        mainContentId: contentId,
        publishStatus: 'preview',
        compiledSceneVersions,
        variantSceneRefs: {
          ...(chapter.variantSceneRefs || {}),
          [contentId]: sceneVariantRefs,
        },
        updatedAt: now
      };

      await saveItem('narrative', 'chapters', updatedChapter);
      await updateNarrativeSummary('chapters', chapterId, updatedChapter);

      toast({
        title: t('chapters.publish.assembled_title'),
        description: t('chapters.publish.assembled_desc', { name: chapter.name })
      });

      // Navigate to /publish-editor/$chapterId
      if (navigateAfter) {
        navigate({
          to: `/publish-editor/${chapterId}`
        });
      }

      return contentId;
    } catch (error) {
      console.error('Failed to assemble final edition:', error);
      toast({
        title: t('common.errors.title'),
        description: t('chapters.publish.assembled_failed'),
        variant: 'destructive'
      });
      return null;
    } finally {
      setIsAssembling(false);
    }
  };

  const updatePublishStatus = async (
    chapterId: string,
    status: 'draft' | 'preview' | 'public'
  ) => {
    if (!novel.id) return;

    setIsUpdatingStatus(true);
    try {
      const chapter = novel.summaries?.chapters?.[chapterId] as NarrativeSummary | undefined;
      if (!chapter) return;
      const publish = httpsCallable(functions, 'setChapterPublishStatus');
      await publish({ ownerId: novel.ownerId, novelId: novel.id, chapterId, status });

      toast({
        title: t('chapters.publish.status_updated_title'),
        description: t('chapters.publish.status_updated_desc', {
          name: chapter.name,
          status: t(`chapters.publish.status_${status}`)
        })
      });
    } catch (error) {
      console.error('Failed to update publish status:', error);
      toast({
        title: t('common.errors.title'),
        description: t('chapters.publish.status_update_failed'),
        variant: 'destructive'
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  return {
    assembleFinalEdition,
    updatePublishStatus,
    isAssembling,
    isUpdatingStatus,
    unreadyData,
    setUnreadyData
  };
}
